import type { VideoEpisodeDoc } from "./video-records.js";
import { buildAliasMap, type RunContext } from "./video-context.js";
import type { VideoSourceEpisodeIdentity } from "./video-source-evolution.js";
import type { ActionV4, ActorV4, ScriptV4 } from "../script/v4/types.js";

/** `model` remains readable for version-1 compatibility; new plans never emit it. */
export type VideoStateEvidenceSelection = "model" | "target_anchor" | "scene_fallback";
export type VideoStateEvidenceFrameStatus = "pending" | "ready" | "unavailable";

export interface VideoStateEvidenceSample {
  evidence_id: string;
  episode: number;
  episode_id: string;
  scene_id: string;
  timestamp: string;
  offset_ms: -1000 | 0 | 1000;
  selection: VideoStateEvidenceSelection;
  source_sha256: string;
  frame_path: string;
  frame_status: VideoStateEvidenceFrameStatus;
  error?: string;
}

export interface VideoActorStateEvidence {
  evidence_id: string;
  asset_kind: "actor";
  actor_id: string;
  actor_name: string;
  state_id: string;
  state_name: string;
  samples: VideoStateEvidenceSample[];
}

export interface VideoActorIdentityEvidence {
  evidence_id: string;
  asset_kind: "actor";
  actor_id: string;
  actor_name: string;
  samples: VideoStateEvidenceSample[];
}

export interface VideoStateEvidenceManifest {
  version: 2;
  generated_at: string;
  source_sha256: string;
  items: VideoActorStateEvidence[];
  identities?: VideoActorIdentityEvidence[];
}

export interface PlannedVideoStateEvidenceSample extends VideoStateEvidenceSample {
  source_path: string;
}

export interface PlannedVideoActorStateEvidence extends Omit<VideoActorStateEvidence, "samples"> {
  samples: PlannedVideoStateEvidenceSample[];
}

export interface PlannedVideoActorIdentityEvidence extends Omit<VideoActorIdentityEvidence, "samples"> {
  samples: PlannedVideoStateEvidenceSample[];
}

export interface VideoStateEvidencePlan {
  version: 2;
  source_sha256: string;
  items: PlannedVideoActorStateEvidence[];
  identities: PlannedVideoActorIdentityEvidence[];
}

interface Candidate {
  actorId: string;
  episode: number;
  episodeId: string;
  sceneId: string;
  timestamp: string;
  milliseconds: number;
  durationMs?: number;
  stateScore: number;
  identityScore: number;
  selection: Exclude<VideoStateEvidenceSelection, "model">;
  sourcePath: string;
  sourceSha256: string;
}

type TimestampMode = "clock" | "legacy_centiseconds";

const IDENTITY_MIN_EVIDENCE_SCORE = 60;
const IDENTITY_BOARD_SIZE = 6;
const FRAME_OFFSETS_MS = [-1_000, 0, 1_000] as const;
const WARDROBE_CUE = /(?:身穿|穿着|服装|造型|衣|裙|袍|衫|夹克|制服|西装|盔甲|装甲|防弹服|套装|裤|靴)/u;
const FRAMING_CUE = /(?:全身|半身|上半身|四分之三身|躯干|胸前|腰部)/u;
const BODY_CUE = /(?:站(?:在|立)|坐(?:在|下|起)|跪(?:在|下|倒)?|蹲(?:在|下)?|走(?:在|出|向)|行走|迈步|奔跑|跳起|跃起|转身|抬头|转头)/u;
const FACE_CUE = /(?:特写|近景|脸|面容|五官|正脸|侧脸|侧前脸|双眼|眼眶|眉头|嘴角)/u;
const BAD_VISUAL_CUE = /(?:背对|背影|远处|极远|模糊|运动模糊|脸被遮|面部遮挡|只露出|纯道具|空镜)/u;
const DETAIL_ONLY_CUE = /(?:双脚|脚部|膝盖|伤口|手部特写)/u;
const OFFSCREEN_SPEECH_CUE = /(?:画外音|旁白|未入镜|镜外|电话音|广播|\bO\.?S\.?\b)/iu;

function norm(value: string): string {
  return value.normalize("NFC").replace(/\s+/g, " ").trim();
}

function clockTimestampMilliseconds(value: string): number | null {
  const match = value.trim().match(/^(?:(\d{1,2}):)?(\d{1,2}):(\d{2})(?:\.(\d{1,3}))?$/);
  if (!match) return null;
  const hours = Number(match[1] ?? 0);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  if (minutes >= 60 && match[1] !== undefined) return null;
  if (seconds >= 60) return null;
  const fraction = String(match[4] ?? "").padEnd(3, "0");
  return ((hours * 60 + minutes) * 60 + seconds) * 1000 + Number(fraction || 0);
}

/**
 * Some imported projects used `seconds:centiseconds` (for example `29:00`
 * means 29 seconds) before Script v4 standardized on clock timestamps. Keep
 * compatibility at the episode level so ordinary `00:33` remains 33 seconds.
 */
function legacyCentisecondTimestampMilliseconds(value: string): number | null {
  const match = value.trim().match(/^(\d+):(\d{2})$/);
  if (!match) return null;
  return Number(match[1]) * 1_000 + Number(match[2]) * 10;
}

function timestampMilliseconds(value: string, mode: TimestampMode = "clock"): number | null {
  return mode === "legacy_centiseconds"
    ? legacyCentisecondTimestampMilliseconds(value)
    : clockTimestampMilliseconds(value);
}

function inSourceRange(milliseconds: number | null, durationMs?: number): milliseconds is number {
  if (milliseconds === null) return false;
  return durationMs === undefined || milliseconds <= durationMs;
}

function episodeTimestampMode(
  episode: ScriptV4["episodes"][number],
  durationMs?: number,
): TimestampMode {
  if (durationMs === undefined) return "clock";
  const timestamps = episode.scenes.flatMap((scene) => scene.actions.map(actionTimestamp)).filter(Boolean);
  const clockValid = timestamps.filter((value) => inSourceRange(clockTimestampMilliseconds(value), durationMs)).length;
  const legacyValid = timestamps.filter((value) => inSourceRange(legacyCentisecondTimestampMilliseconds(value), durationMs)).length;
  // Ties intentionally remain standard clock timestamps. A legacy episode is
  // selected only when the source duration proves it explains more Actions.
  return legacyValid > clockValid ? "legacy_centiseconds" : "clock";
}

function timestampFromMilliseconds(milliseconds: number): string {
  const minutes = Math.floor(milliseconds / 60_000);
  const seconds = Math.floor((milliseconds % 60_000) / 1_000);
  const fraction = milliseconds % 1_000;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${String(fraction).padStart(3, "0")}`;
}

function actionTimestamp(action: ActionV4): string {
  const value = action.extend?.timestamp;
  return typeof value === "string" ? value.trim() : "";
}

function actorSurfaces(actor: ActorV4, contextAliases: Map<string, string>): string[] {
  const names = new Set([actor.actor_name, ...(actor.aliases ?? [])].map(norm).filter(Boolean));
  for (const [alias, canonical] of contextAliases) {
    if (norm(canonical) === norm(actor.actor_name)) names.add(norm(alias));
  }
  return [...names].sort((left, right) => right.length - left.length);
}

function mentionIndex(content: string, surfaces: readonly string[]): number {
  const indices = surfaces.map((surface) => content.indexOf(surface)).filter((index) => index >= 0);
  return indices.length > 0 ? Math.min(...indices) : -1;
}

function visibleNames(content: string, actors: readonly ActorV4[], surfacesByActor: Map<string, string[]>): number {
  return actors.filter((actor) => mentionIndex(content, surfacesByActor.get(actor.actor_id) ?? []) >= 0).length;
}

function scoreActionCandidate({
  action,
  actor,
  actorCount,
  allActors,
  surfaces,
  surfacesByActor,
  stateName,
}: {
  action: ActionV4;
  actor: ActorV4;
  actorCount: number;
  allActors: readonly ActorV4[];
  surfaces: readonly string[];
  surfacesByActor: Map<string, string[]>;
  stateName: string;
}): { stateScore: number; identityScore: number; selection: Candidate["selection"] } | null {
  const narrative = action.type === "action" || action.type === "transition";
  const content = norm(narrative
    ? action.content
    : "parenthetical" in action ? action.parenthetical ?? "" : "");
  const directIndex = mentionIndex(content, surfaces);
  const directSubject = directIndex >= 0 && directIndex <= 12;
  const directMention = directIndex >= 0;

  // For a visual contact sheet, a normal spoken line in a scene where the final
  // Script already binds this actor + state is the best cheap proxy for a clean
  // character shot. It is not identity proof (reverse shots still exist), so
  // the client keeps the neighbouring frames and human annotation path. Explicit
  // off-screen/voice-over speech is never used as a visual anchor.
  const actorSpeaker = action.type === "dialogue" && "speaker" in action && "actor_id" in action.speaker
    && action.speaker.actor_id === actor.actor_id;
  const visibleSpeaker = actorSpeaker && !OFFSCREEN_SPEECH_CUE.test(content);
  const singleActorVisibleFallback = actorCount === 1 && (
    (narrative && content.length > 0)
    || (visibleSpeaker && content.length > 0 && (BODY_CUE.test(content) || FACE_CUE.test(content)))
  );
  if (!directMention && !visibleSpeaker && !singleActorVisibleFallback) return null;

  const selection: Candidate["selection"] = directSubject || visibleSpeaker ? "target_anchor" : "scene_fallback";
  // Deliberately keep ordinary on-screen dialogue above even a very descriptive
  // wardrobe Action: the scene-level state reference already establishes which
  // outfit is active, while the speaking beat is more likely to show the person.
  let stateScore = visibleSpeaker ? 250 : directSubject ? 65 : directMention ? 50 : 45;
  let identityScore = visibleSpeaker ? 250 : directSubject ? 65 : directMention ? 50 : 45;
  if (actorCount === 1) {
    stateScore += 20;
    identityScore += 20;
  }
  const normalizedState = norm(stateName);
  if (normalizedState && normalizedState !== "default" && normalizedState !== "默认" && content.includes(normalizedState)) {
    stateScore += 35;
  }
  if (WARDROBE_CUE.test(content)) stateScore += 30;
  if (FRAMING_CUE.test(content)) {
    stateScore += 25;
    identityScore += 15;
  } else if (BODY_CUE.test(content)) {
    stateScore += 15;
    identityScore += 5;
  }
  if (FACE_CUE.test(content)) {
    identityScore += 30;
    if (!FRAMING_CUE.test(content)) stateScore -= 20;
  }
  if (BAD_VISUAL_CUE.test(content)) {
    stateScore -= 70;
    identityScore -= 70;
  }
  if (DETAIL_ONLY_CUE.test(content)) stateScore -= 40;
  if (visibleNames(content, allActors, surfacesByActor) >= 2) {
    stateScore -= 15;
    identityScore -= 15;
  }
  return { stateScore, identityScore, selection };
}

function scoreStateCoverageFallback(
  action: ActionV4,
  allActors: readonly ActorV4[],
  surfacesByActor: Map<string, string[]>,
): { stateScore: number; identityScore: number; selection: Candidate["selection"] } | null {
  const narrative = action.type === "action" || action.type === "transition";
  const parenthetical = "parenthetical" in action ? norm(action.parenthetical ?? "") : "";
  if (!narrative && OFFSCREEN_SPEECH_CUE.test(parenthetical)) return null;
  const content = norm(narrative ? action.content : parenthetical);

  // A scene-level timestamp cannot prove identity, but it is still a useful
  // last-resort state anchor when the final scene reference binds one costume to
  // the actor. Keep it below every actor-specific candidate so quality scoring
  // only chooses the best frame; it must not make a referenced costume disappear.
  let stateScore = narrative ? 0 : -40;
  stateScore -= visibleNames(content, allActors, surfacesByActor) * 20;
  if (WARDROBE_CUE.test(content)) stateScore += 10;
  if (FRAMING_CUE.test(content)) stateScore += 10;
  else if (BODY_CUE.test(content)) stateScore += 5;
  if (BAD_VISUAL_CUE.test(content)) stateScore -= 20;
  if (DETAIL_ONLY_CUE.test(content)) stateScore -= 10;
  return { stateScore, identityScore: Number.NEGATIVE_INFINITY, selection: "scene_fallback" };
}

function effectiveScore(
  candidate: Candidate,
  scoreKey: "stateScore" | "identityScore",
  owners: Map<string, Set<string>>,
): number {
  const shared = (owners.get(`${candidate.episode}:${candidate.milliseconds}`)?.size ?? 0) > 1;
  return candidate[scoreKey] - (shared ? 35 : 0);
}

function bestCandidates(
  candidates: readonly Candidate[],
  scoreKey: "stateScore" | "identityScore",
  owners: Map<string, Set<string>>,
  limit: number,
  minimumScore: number | null,
): Candidate[] {
  const deduplicated = new Map<string, Candidate>();
  for (const candidate of candidates) {
    const key = `${candidate.episode}:${candidate.milliseconds}`;
    const existing = deduplicated.get(key);
    if (!existing || effectiveScore(candidate, scoreKey, owners) > effectiveScore(existing, scoreKey, owners)) {
      deduplicated.set(key, candidate);
    }
  }
  const ranked = [...deduplicated.values()]
    .sort((left, right) => effectiveScore(right, scoreKey, owners) - effectiveScore(left, scoreKey, owners)
      || left.episode - right.episode
      || left.milliseconds - right.milliseconds)
    .filter((candidate) => minimumScore === null || effectiveScore(candidate, scoreKey, owners) >= minimumScore);

  // A useful contact sheet needs visual variety, not six near-identical frames
  // from one shot. Prefer different scenes; only reuse a scene when its anchors
  // are at least three seconds apart and the board still has empty cells.
  const selected: Candidate[] = [];
  for (const candidate of ranked) {
    const tooClose = selected.some((entry) => entry.episode === candidate.episode
      && entry.sceneId === candidate.sceneId
      && Math.abs(entry.milliseconds - candidate.milliseconds) < 3_000);
    if (tooClose) continue;
    selected.push(candidate);
    if (selected.length >= limit) break;
  }
  return selected;
}

function bestCandidate(
  candidates: readonly Candidate[],
  scoreKey: "stateScore" | "identityScore",
  owners: Map<string, Set<string>>,
): Candidate | null {
  // State evidence is coverage-first: every actor-specific or neutral visual
  // candidate remains eligible, while scores only determine which one wins.
  return bestCandidates(candidates, scoreKey, owners, 1, null)[0] ?? null;
}

function timestampFilePart(timestamp: string): string {
  return timestamp.replace(/[^0-9]+/g, "-").replace(/^-|-$/g, "") || "unknown";
}

/**
 * Build evidence only after final Script v4 identities are stable. Gemini does
 * not return any evidence fields: candidates come from final Action timestamps,
 * visible-action text and single-person scene ranges, then ffmpeg materializes
 * the selected frames locally.
 */
export function buildVideoStateEvidencePlan(
  docs: readonly VideoEpisodeDoc[],
  script: ScriptV4,
  context: RunContext,
  sources: readonly VideoSourceEpisodeIdentity[],
  sourceSha256: string,
): VideoStateEvidencePlan {
  const sourceByEpisode = new Map(sources.map((source) => [source.episode, source]));
  const contextAliases = buildAliasMap(context.registry);
  const actorById = new Map<string, ActorV4>(script.actors.map((actor) => [actor.actor_id, actor]));
  const surfacesByActor = new Map(script.actors.map((actor) => [actor.actor_id, actorSurfaces(actor, contextAliases)]));
  const appearancesByActor = new Map<string, number>();
  const identityCandidates = new Map<string, Candidate[]>();
  const stateCandidates = new Map<string, Candidate[]>();
  const owners = new Map<string, Set<string>>();

  for (let episodeIndex = 0; episodeIndex < script.episodes.length; episodeIndex += 1) {
    const episode = script.episodes[episodeIndex]!;
    const episodeNo = episodeIndex + 1;
    const source = sourceByEpisode.get(episodeNo);
    if (!source) continue;
    const timestampMode = episodeTimestampMode(episode, source.duration_ms);
    for (let sceneIndex = 0; sceneIndex < episode.scenes.length; sceneIndex += 1) {
      const scene = episode.scenes[sceneIndex]!;
      const actors = scene.actors.map((ref) => actorById.get(ref.actor_id)).filter((actor): actor is ActorV4 => Boolean(actor));
      for (const ref of scene.actors) {
        appearancesByActor.set(ref.actor_id, (appearancesByActor.get(ref.actor_id) ?? 0) + 1);
        const actor = actorById.get(ref.actor_id);
        if (!actor) continue;
        const surfaces = surfacesByActor.get(actor.actor_id) ?? [actor.actor_name];
        const local: Candidate[] = [];
        const stateId = ref.state_ids.length === 1 ? ref.state_ids[0]! : null;
        const state = stateId ? actor.states.find((candidate) => candidate.state_id === stateId) : null;
        for (const action of scene.actions) {
          const timestamp = actionTimestamp(action);
          const milliseconds = timestampMilliseconds(timestamp, timestampMode);
          if (!inSourceRange(milliseconds, source.duration_ms)) continue;
          const actorSpecific = scoreActionCandidate({
            action,
            actor,
            actorCount: actors.length,
            allActors: actors,
            surfaces,
            surfacesByActor,
            stateName: state?.state_name ?? "default",
          });
          const scored = actorSpecific ?? (state
            ? scoreStateCoverageFallback(action, actors, surfacesByActor)
            : null);
          if (!scored) continue;
          const candidate: Candidate = {
            actorId: actor.actor_id,
            episode: episodeNo,
            episodeId: episode.episode_id,
            sceneId: scene.scene_id,
            timestamp: timestampFromMilliseconds(milliseconds),
            milliseconds,
            durationMs: source.duration_ms,
            stateScore: scored.stateScore,
            identityScore: scored.identityScore,
            selection: scored.selection,
            sourcePath: source.path,
            sourceSha256: source.sha256,
          };
          local.push(candidate);
          if (actorSpecific) {
            const ownerKey = `${episodeNo}:${milliseconds}`;
            const ownerSet = owners.get(ownerKey) ?? new Set<string>();
            ownerSet.add(actor.actor_id);
            owners.set(ownerKey, ownerSet);
          }
        }
        if (local.length > 0) {
          const identity = identityCandidates.get(actor.actor_id) ?? [];
          identity.push(...local);
          identityCandidates.set(actor.actor_id, identity);
          // A scene-level state_ids set is unordered. Only a single state can be
          // attributed to every Action in the scene without inventing chronology.
          if (state) {
            const key = `${actor.actor_id}:${state.state_id}`;
            const values = stateCandidates.get(key) ?? [];
            values.push(...local);
            stateCandidates.set(key, values);
          }
        }
      }
    }
  }

  const compareActorPriority = (leftId: string, rightId: string): number => {
    const left = actorById.get(leftId);
    const right = actorById.get(rightId);
    const leftBackground = left?.importance === "background" ? 1 : 0;
    const rightBackground = right?.importance === "background" ? 1 : 0;
    return leftBackground - rightBackground
      || (appearancesByActor.get(rightId) ?? 0) - (appearancesByActor.get(leftId) ?? 0)
      || (left?.actor_name ?? leftId).localeCompare(right?.actor_name ?? rightId, "zh-CN");
  };

  const items = [...stateCandidates.entries()].flatMap(([key, values]): PlannedVideoActorStateEvidence[] => {
    const [actorId, stateId] = key.split(":");
    const actor = actorId ? actorById.get(actorId) : undefined;
    const state = actor?.states.find((candidate) => candidate.state_id === stateId);
    const anchor = bestCandidate(values, "stateScore", owners);
    if (!actor || !state || !anchor) return [];
    const evidenceId = `state:${actor.actor_id}:${state.state_id}`;
    const samples = FRAME_OFFSETS_MS.map((offset) => {
      const lastSeekableMillisecond = anchor.durationMs === undefined
        ? Number.POSITIVE_INFINITY
        : Math.max(0, anchor.durationMs - 50);
      const milliseconds = Math.min(lastSeekableMillisecond, Math.max(0, anchor.milliseconds + offset));
      const timestamp = timestampFromMilliseconds(milliseconds);
      return {
        evidence_id: `${evidenceId}:${anchor.episode}:${timestamp}`,
        episode: anchor.episode,
        episode_id: anchor.episodeId,
        scene_id: anchor.sceneId,
        timestamp,
        offset_ms: offset,
        selection: anchor.selection,
        source_sha256: anchor.sourceSha256,
        source_path: anchor.sourcePath,
        frame_path: `evidence/frames/${actor.actor_id}/${state.state_id}/ep_${String(anchor.episode).padStart(3, "0")}_${timestampFilePart(timestamp)}.jpg`,
        frame_status: "pending" as const,
      };
    }).filter((sample, index, all) => all.findIndex((candidate) => candidate.timestamp === sample.timestamp) === index);
    return [{
      evidence_id: evidenceId,
      asset_kind: "actor",
      actor_id: actor.actor_id,
      actor_name: actor.actor_name,
      state_id: state.state_id,
      state_name: state.state_name,
      samples,
    }];
  }).sort((left, right) => compareActorPriority(left.actor_id, right.actor_id)
    || left.state_name.localeCompare(right.state_name, "zh-CN"));

  const identities = [...identityCandidates.entries()].flatMap(([actorId, values]): PlannedVideoActorIdentityEvidence[] => {
    const actor = actorById.get(actorId);
    const anchors = bestCandidates(
      values,
      "identityScore",
      owners,
      IDENTITY_BOARD_SIZE,
      IDENTITY_MIN_EVIDENCE_SCORE,
    );
    if (!actor || anchors.length === 0) return [];
    const evidenceId = `identity:${actor.actor_id}`;
    return [{
      evidence_id: evidenceId,
      asset_kind: "actor",
      actor_id: actor.actor_id,
      actor_name: actor.actor_name,
      // Ranked, cross-scene identity/appearance frames. The client renders these
      // as a compact 3x2 contact sheet; the first sample remains the single-frame
      // fallback for older clients and inline hover cards.
      samples: anchors.map((anchor) => ({
        evidence_id: `${evidenceId}:${anchor.episode}:${anchor.timestamp}`,
        episode: anchor.episode,
        episode_id: anchor.episodeId,
        scene_id: anchor.sceneId,
        timestamp: anchor.timestamp,
        offset_ms: 0,
        selection: anchor.selection,
        source_sha256: anchor.sourceSha256,
        source_path: anchor.sourcePath,
        frame_path: `evidence/frames/${actor.actor_id}/identity/ep_${String(anchor.episode).padStart(3, "0")}_${timestampFilePart(anchor.timestamp)}.jpg`,
        frame_status: "pending" as const,
      })),
    }];
  }).sort((left, right) => compareActorPriority(left.actor_id, right.actor_id));

  return { version: 2, source_sha256: sourceSha256, items, identities };
}

export function parseVideoEvidenceTimestamp(value: string): number | null {
  return timestampMilliseconds(value);
}
